import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/api';
import { StatCard, Card, fmtDate } from '../components/UI';

const COLORS = ['#4ADE80','#60A5FA','#F472B6','#FBBF24','#A78BFA','#34D399','#FB923C','#E879F9'];
const initials = (name) => (name || 'MR').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

const Stars = ({ n }) => (
  <span style={{ color: '#F59E0B', fontSize: 15, letterSpacing: 1 }}>
    {'★'.repeat(Math.min(n, 5))}{'☆'.repeat(Math.max(0, 5 - n))}
  </span>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending:  { bg: '#FEF3C7', color: '#B45309', label: 'Pending' },
    approved: { bg: '#D1FAE5', color: '#065F46', label: '✓ Approved' },
    rejected: { bg: '#FEE2E2', color: '#991B1B', label: '✗ Rejected' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
      {s.label}
    </span>
  );
};

const DetailModal = ({ fb, onClose, onUpdate, onDelete }) => {
  const [displayName, setDisplayName] = useState(fb.displayName || fb.name || '');
  const [displayRole, setDisplayRole] = useState(fb.displayRole || fb.company || '');
  const [saving, setSaving] = useState(false);

  const act = async (status) => {
    setSaving(true);
    try {
      const updated = await adminApi.updateFeedback(fb._id, { status, displayName, displayRole });
      onUpdate(updated);
      onClose();
    } finally { setSaving(false); }
  };

  const inp = { width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginTop: 4 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#0F172A,#1E293B)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: '50%', background: COLORS[fb.name?.charCodeAt(0) % COLORS.length || 0], display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 15, color: '#111827', flexShrink: 0 }}>
              {initials(fb.name)}
            </div>
            <div>
              <div style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>{fb.name || 'Anonymous'}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{fb.email || 'No email'} · {fb.plan === 'pro' ? '⭐ Pro' : '🆓 Free'}{fb.company ? ` · ${fb.company}` : ''}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: 'white', fontSize: 18, cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Rating + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Stars n={fb.rating} />
            <StatusBadge status={fb.status} />
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF' }}>{fmtDate(fb.createdAt)}</span>
          </div>

          {/* Message */}
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#374151', lineHeight: 1.7, marginBottom: 20, whiteSpace: 'pre-wrap' }}>
            "{fb.message}"
          </div>

          {/* Display name & role for landing page */}
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Landing Page Display (edit before approving)
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Display Name</label>
              <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. Ahmed R." style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Role / Company</label>
              <input value={displayRole} onChange={e => setDisplayRole(e.target.value)} placeholder="e.g. Software Engineer at Google" style={inp} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => onDelete(fb._id)}
              style={{ padding: '10px 16px', border: '1.5px solid #FECACA', borderRadius: 9, background: '#FEF2F2', color: '#DC2626', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              Delete
            </button>
            <div style={{ flex: 1 }} />
            {fb.status !== 'rejected' && (
              <button onClick={() => act('rejected')} disabled={saving}
                style={{ padding: '10px 20px', border: '1.5px solid #E5E7EB', borderRadius: 9, background: 'white', color: '#6B7280', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                Reject
              </button>
            )}
            {fb.status !== 'approved' && (
              <button onClick={() => act('approved')} disabled={saving}
                style={{ padding: '10px 24px', border: 'none', borderRadius: 9, background: 'linear-gradient(135deg,#059669,#047857)', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(5,150,105,0.35)' }}>
                {saving ? 'Saving…' : '✓ Approve & Show on Site'}
              </button>
            )}
            {fb.status === 'approved' && (
              <button onClick={() => act('approved')} disabled={saving}
                style={{ padding: '10px 24px', border: 'none', borderRadius: 9, background: 'linear-gradient(135deg,#1D4ED8,#6366F1)', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                {saving ? 'Saving…' : 'Update Display Info'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FeedbackPage = () => {
  const [stats, setStats]     = useState(null);
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        adminApi.getFeedbackStats(),
        adminApi.getFeedback({ status: filter, search }),
      ]);
      setStats(s);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (updated) => {
    setItems(prev => prev.map(i => i._id === updated._id ? updated : i));
    if (stats) {
      adminApi.getFeedbackStats().then(setStats).catch(() => {});
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback permanently?')) return;
    await adminApi.deleteFeedback(id);
    setItems(prev => prev.filter(i => i._id !== id));
    setSelected(null);
    adminApi.getFeedbackStats().then(setStats).catch(() => {});
  };

  const STATUS_FILTERS = ['all', 'pending', 'approved', 'rejected'];

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, sans-serif' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#0F172A' }}>Customer Feedback</h1>
        <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>Review user feedback and approve testimonials to display on the landing page.</p>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
          <StatCard icon="💬" label="Total"    value={stats.total}    sub="all submissions"      color="#6366F1" />
          <StatCard icon="⏳" label="Pending"  value={stats.pending}  sub="awaiting review"      color="#F59E0B" />
          <StatCard icon="✅" label="Approved" value={stats.approved} sub="shown on landing page" color="#059669" />
          <StatCard icon="✗"  label="Rejected" value={stats.rejected} sub="not shown"            color="#EF4444" />
        </div>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => setFilter(s)}
                style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${filter === s ? '#EF1D28' : '#E5E7EB'}`, background: filter === s ? '#FFF1F2' : 'white', color: filter === s ? '#C5161D' : '#6B7280', fontWeight: filter === s ? 700 : 500, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize' }}>
                {s}
              </button>
            ))}
          </div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, email, message…"
            style={{ flex: 1, minWidth: 200, border: '1.5px solid #E5E7EB', borderRadius: 9, padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
          />
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>{total} results</span>
        </div>
      </Card>

      {/* Table */}
      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#9CA3AF' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>💬</div>
            <div style={{ color: '#9CA3AF', fontSize: 14 }}>No feedback found</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #F1F5F9' }}>
                {['User', 'Rating', 'Feedback', 'Plan', 'Status', 'Date', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((fb, i) => (
                <tr key={fb._id} style={{ borderBottom: '1px solid #F9FAFB', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                  onClick={() => setSelected(fb)}>
                  <td style={{ padding: '12px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 12, color: '#111827', flexShrink: 0 }}>
                        {initials(fb.name)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{fb.name || 'Anonymous'}</div>
                        <div style={{ fontSize: 11, color: '#9CA3AF' }}>{fb.company || fb.email || '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 12px' }}><Stars n={fb.rating} /></td>
                  <td style={{ padding: '12px 12px', maxWidth: 280 }}>
                    <div style={{ fontSize: 12, color: '#374151', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.5 }}>
                      {fb.message}
                    </div>
                  </td>
                  <td style={{ padding: '12px 12px' }}>
                    <span style={{ background: fb.plan === 'pro' ? '#ECFDF5' : '#FEF3C7', color: fb.plan === 'pro' ? '#059669' : '#B45309', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                      {fb.plan === 'pro' ? '⭐ Pro' : '🆓 Free'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 12px' }}><StatusBadge status={fb.status} /></td>
                  <td style={{ padding: '12px 12px', fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{fmtDate(fb.createdAt)}</td>
                  <td style={{ padding: '12px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); setSelected(fb); }}
                      style={{ background: '#F1F5F9', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                      Review →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selected && (
        <DetailModal
          fb={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
